<template>
<div v-if="loaded">
    <div class="mx-4 my-2" v-if="!can_update_weekly_schedule">
        <subscription-notice />
    </div>

    <div class="mx-4 mt-8" :class="!can_update_daily_schedule ? 'opacity-50' : ''">
        <div class="rounded w-11/12 md:w-1/2 mx-auto my-2 text-sm bg-primary-lightest text-primary p-3">
            <p>All times are in the <strong>{{ timezone }}</strong> timezone. If your timezone ever changes, stop and start Mailman again to reset to the new timezone.</p>
        </div>
        <div class="rounded w-11/12 md:w-1/2 mx-auto bg-gray-light">
            <input type="radio" id="interval" name="type" value="interval" class="hidden" v-model="type" :disabled="!can_update_daily_schedule">
            <label for="interval" class="cursor-pointer">
                <div class="flex items-start px-3 py-6">
                    <span class="indicator w-4 h-4 inline-block rounded-full border border-primary flex-no-shrink bg-primary-lighter"></span>
                    <div class="mx-3 flex-1 -mt-1">
                        <h3 class="text-sm font-bold">Hours of interval</h3>
                        <p class="text-gray-darker text-sm">eg. deliver emails every 4 hours in a day</p>
                    </div>
                    <div class="inline-block relative">
                        <select
                            class="block appearance-none w-full bg-white border border-gray-dark border-b-2 cursor-pointer px-4 py-2 pr-6 rounded leading-tight focus:outline-none focus:ring text-sm"
                            :disabled="!can_update_daily_schedule"
                            v-model="value.interval"
                        >
                            <option v-for="option in options.interval" :key="'interval-' + option" :value="option">{{ option }} hours</option>
                        </select>
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                            <font-awesome-icon :icon="['fas', 'caret-down']" class=""></font-awesome-icon>
                        </div>
                    </div>
                </div>
            </label>
        </div>
    </div>

    <div class="mx-4 mt-2" :class="!can_update_daily_schedule ? 'opacity-50' : ''">
        <div class="rounded w-11/12 md:w-1/2 mx-auto bg-gray-light">
            <input type="radio" id="count" name="type" value="count" class="hidden" v-model="type" :disabled="!can_update_daily_schedule">
            <label for="count" class="cursor-pointer">
                <div class="flex items-start px-3 py-6">
                    <span class="indicator w-4 h-4 inline-block rounded-full border border-primary flex-no-shrink bg-primary-lighter"></span>
                    <div class="mx-3 flex-1 -mt-1">
                        <h3 class="text-sm font-bold">Number of times</h3>
                        <p class="text-gray-darker text-sm">eg. send me emails 3 times in a day at equal intervals</p>
                    </div>
                    <div class="inline-block relative">
                        <select
                            class="block appearance-none w-full bg-white border border-gray-dark border-b-2 cursor-pointer px-4 py-2 pr-6 rounded leading-tight focus:outline-none focus:ring text-sm"
                            :disabled="!can_update_daily_schedule"
                            v-model="value.count"
                        >
                            <option v-for="option in options.count" :key="'count-' + option" :value="option">{{ option }} times</option>
                        </select>
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                            <font-awesome-icon :icon="['fas', 'caret-down']" class=""></font-awesome-icon>
                        </div>
                    </div>
                </div>
            </label>
        </div>
    </div>

    <div class="mx-4 mt-2" :class="!can_update_daily_schedule ? 'opacity-50' : ''">
        <div class="rounded w-11/12 md:w-1/2 mx-auto bg-gray-light">
            <input type="radio" id="hours" name="type" value="hours" class="hidden" v-model="type" :disabled="!can_update_daily_schedule">
            <label for="hours" class="cursor-pointer">
                <div class="flex items-start px-3 py-6">
                    <span class="indicator w-4 h-4 inline-block rounded-full border border-primary flex-no-shrink bg-primary-lighter"></span>
                    <div class="mx-3 flex-1 -mt-1">
                        <h3 class="text-sm font-bold">Custom daily schedule</h3>
                        <p class="text-gray-darker text-sm">eg. deliver emails at 1pm and 6pm</p>
                        <div class="mt-2 flex flex-wrap items-center">
                            <span v-for="(hour, index) in value.hours" :key="index">
                                <vue-timepicker
                                    :disabled="!can_update_daily_schedule"
                                    format="hh:mma"
                                    placeholder="hh:mm"
                                    :minute-interval="10"
                                    close-on-complete
                                    hour-label="hour"
                                    minute-label="minute"
                                    apm-label=" "
                                    auto-scroll
                                    v-model="hour.raw"
                                />
                            </span>
                            <button class="bg-primary-lighter text-primary h-8 w-8 rounded-full focus:outline-none active:outline-none" @click="addNewHour">
                                <font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </label>
        </div>
    </div>

    <div class="mx-4 mt-2" :class="!can_update_weekly_schedule ? 'opacity-50' : ''">
        <div class="rounded w-11/12 md:w-1/2 mx-auto bg-gray-light">
            <input type="radio" id="hours_by_days" name="type" value="hours_by_days" class="hidden" v-model="type" :disabled="!can_update_weekly_schedule">
            <label for="hours_by_days" class="cursor-pointer">
                <div class="flex items-start px-3 py-6">
                    <span class="indicator w-4 h-4 inline-block rounded-full border border-primary flex-no-shrink bg-primary-lighter"></span>
                    <div class="mx-3 flex-1 -mt-1">
                        <h3 class="text-sm font-bold">Custom weekly schedule</h3>
                        <p class="text-gray-darker text-sm">eg. deliver emails at 1pm and 6pm only on Mondays</p>
                        <div v-for="(day, dayIndex) in value.hoursByDays" :key="day.label" class=" border border-gray-darker rounded my-4 pb-2 relative" v-show="type === 'hours_by_days'">
                            <div class="absolute text-xs bg-gray-light  rounded px-1 -mt-2 ml-2 uppercase">
                                <span>{{ day.label }}</span>
                            </div>
                            <div class="mt-4 px-2 flex flex-wrap items-center">
                                <span v-for="(hour, index) in value.hoursByDays[dayIndex].hours" :key="index">
                                    <vue-timepicker
                                        :disabled="!can_update_weekly_schedule"
                                        format="hh:mma"
                                        placeholder="hh:mm"
                                        :minute-interval="10"
                                        close-on-complete
                                        hour-label="hour"
                                        minute-label="minute"
                                        apm-label=" "
                                        auto-scroll
                                        v-model="hour.raw"
                                    />
                                </span>
                                <button class="bg-primary-lighter text-primary h-8 w-8 rounded-full focus:outline-none active:outline-none mr-2" @click="addNewHourForDay(dayIndex)">
                                    <font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
                                </button>
                                <div class="inline-block relative">
                                    <select
                                        class="block appearance-none w-full bg-white border border-gray-dark border-b-2 cursor-pointer p-2 leading-tight rounded focus:outline-none focus:ring text-sm"
                                        :disabled="!can_update_weekly_schedule"
                                        @change="copyDaySchedule(day.dayOfWeek, $event)"
                                    >
                                        <option :value="null">Copy from...</option>
                                        <option v-for="option in options.daysOfWeek" :key="'day-of-week-' + option.dayOfWeek" :value="option.dayOfWeek">{{ option.label }}</option>
                                    </select>
                                    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
                                        <font-awesome-icon :icon="['fas', 'caret-down']" class=""></font-awesome-icon>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </label>
        </div>
    </div>

    <div class="mx-4 mt-8 pt-8 border-t border-gray-dark" :class="!can_update_weekly_schedule ? 'opacity-50' : ''">
        <div class="rounded w-11/12 md:w-1/2 mx-auto bg-gray-light">
            <div class="flex items-center px-3 py-6">
                <div class="mx-3 flex-1 -mt-1">
                    <h3 class="text-sm font-bold">Do Not Disturb</h3>
                    <p class="text-gray-darker text-sm">No deliveries will be made during this period</p>
                </div>
                <p-check class="p-switch p-fill" color="primary" v-model="dnd.is_on" :disabled="!can_update_weekly_schedule"></p-check>
            </div>
            <div class="flex flex-wrap items-center px-6 pb-3 -mt-3" v-if="dnd.is_on">
                <span class="text-sm mr-1">From</span>
                <span>
                    <vue-timepicker
                        :disabled="!can_update_weekly_schedule"
                        format="hh:mma"
                        placeholder="from"
                        :minute-interval="10"
                        hour-label="hour"
                        minute-label="minute"
                        apm-label=" "
                        auto-scroll
                        hide-clear-button
                        v-model="dnd.starts_at.raw"
                    />
                </span>
                <span class="text-sm mr-1">To</span>
                <span>
                    <vue-timepicker
                        :disabled="!can_update_weekly_schedule"
                        format="hh:mma"
                        placeholder="to"
                        :minute-interval="10"
                        hour-label="hour"
                        minute-label="minute"
                        apm-label=" "
                        auto-scroll
                        hide-clear-button
                        v-model="dnd.ends_at.raw"
                    />
                </span>
            </div>
        </div>
    </div>

    <div class="mx-4 mt-8 pt-8 border-t border-gray-dark" :class="!can_update_weekly_schedule ? 'opacity-50' : ''">
        <div class="rounded w-11/12 md:w-1/2 mx-auto bg-gray-light">
            <div class="flex items-center px-3 py-6">
                <div class="mx-3 flex-1 -mt-1">
                    <h3 class="text-sm font-bold">Daily Summary</h3>
                    <p class="text-gray-darker text-sm">We'll send you a recap of your inbox everyday at this time</p>
                </div>
                <p-check class="p-switch p-fill" color="primary" v-model="summary.is_on" :disabled="!can_update_weekly_schedule"></p-check>
            </div>
            <div class="flex flex-wrap items-center px-6 pb-3 -mt-3" v-if="summary.is_on">
                <span>
                    <vue-timepicker
                        :disabled="!can_update_weekly_schedule"
                        format="hh:mma"
                        placeholder="hh:mm"
                        :minute-interval="10"
                        hour-label="hour"
                        minute-label="minute"
                        apm-label=" "
                        auto-scroll
                        hide-clear-button
                        v-model="summary.at.raw"
                    />
                </span>
            </div>
        </div>
    </div>


    <div class="mx-4 mt-12 pb-12">
        <div class="w-11/12 md:w-1/2 mx-auto">
            <progressable-button
                type="submit"
                class="py-2 px-4 rounded-md bg-primary text-white font-bold"
                :loading="loading"
                :state="loadingState"
                @click.native="save"
            >
                Save changes
            </progressable-button>
        </div>
    </div>
</div>
<div v-else>
    <loading />
</div>
</template>

<script>
export default {
    data() {
        return {
            current   : {},
            can_update_weekly_schedule: false,
            can_update_daily_schedule: false,
            type      : 'count',
            value     : {
                count      : 2,
                interval   : 2,
                hours      : Schedule.makeRawHours([]),
                hoursByDays: Schedule.makeRawHoursByDays([]),
            },
            options: {
                count   : [2, 3, 4, 6, 8, 12],
                interval: [2, 3, 4, 6, 8, 12],
                hours   : ['01:00pm'],
                daysOfWeek: [
                    {dayOfWeek: 1, label: 'Monday'},
                    {dayOfWeek: 2, label: 'Tuesday'},
                    {dayOfWeek: 3, label: 'Wednesday'},
                    {dayOfWeek: 4, label: 'Thursday'},
                    {dayOfWeek: 5, label: 'Friday'},
                    {dayOfWeek: 6, label: 'Saturday'},
                    {dayOfWeek: 7, label: 'Sunday'},
                ]
            },
            summary     : {
                is_on: false,
                at   : Schedule.makeRawHour('04:30pm'),
            },
            dnd: {
                is_on    : false,
                starts_at: Schedule.makeRawHour('05:30pm'),
                ends_at  : Schedule.makeRawHour('07:30am'),
            },
            utc_offset  : new Date().getTimezoneOffset(),
            loaded      : false,
            loading     : false,
            loadingState: '...'
        };
    },
    computed: {
        timezone() {
            let sign = this.utc_offset < 0 ? '+' : '-';
            let absoluteOffset = Math.abs(this.utc_offset);
            let offsetInHours = (absoluteOffset/60);

            let hours = (Math.floor(absoluteOffset/60)).toString().padStart(2, '0');
            let minutes = (Math.floor(absoluteOffset%60)).toString().padStart(2, '0');

            return `GMT${sign}${hours}:${minutes}`;
        },
    },
    mounted() {
        this.loading = true;

        axios.get(`/api/mailbox`)
            .then((response) => {
                this.current       = response.data.data.schedule;
                if (response.data.data.summary !== null) {
                    this.summary.is_on = true;
                    this.summary.at    = Schedule.makeRawHour(response.data.data.summary);
                }
                if (response.data.data.dnd.starts_at !== null) {
                    this.dnd.is_on     = true;
                    this.dnd.starts_at = Schedule.makeRawHour(response.data.data.dnd.starts_at);
                    this.dnd.ends_at   = Schedule.makeRawHour(response.data.data.dnd.ends_at);
                }
                if (response.data.data.utc_offset !== 0) {
                    this.utc_offset = response.data.data.utc_offset;
                }
                this.can_update_daily_schedule = User.permissions.use_feature_daily_delivery;
                this.can_update_weekly_schedule = User.permissions.use_feature_weekly_delivery;
                this.reset();
                this.loaded = true;
            })
            .catch((err) => {
                console.error(err);
            })
            .then(() => {
                this.loading = false;
            });
    },
    methods: {
        addNewHour() {
            if (!this.can_update_daily_schedule) return;

            this.value.hours.push(Schedule.emptyHour());
        },
        addNewHourForDay(index) {
            if (!this.can_update_weekly_schedule) return;

            this.value.hoursByDays[index].hours.push(Schedule.emptyHour());
        },
        copyDaySchedule(targetDayOfWeek, $event) {
            let sourceDayofWeek = parseInt($event.target.value);
            if (sourceDayofWeek === NaN) return;

            let sourceIndex = _.findIndex(this.value.hoursByDays, {dayOfWeek: sourceDayofWeek}),
                targetIndex = _.findIndex(this.value.hoursByDays, {dayOfWeek: targetDayOfWeek});

            if (sourceIndex == -1 || targetIndex == -1) return;

            this.value.hoursByDays[targetIndex].hours = _.cloneDeep(this.value.hoursByDays[sourceIndex].hours);
        },
        save() {
            this.loadingState = 'Saving...';
            this.loading = true;

            axios.put(`/api/mailbox`, {
                schedule: this.makeSchedule(),
                summary : this.makeSummary(),
                dnd     : this.makeDND(),
            })
            .then(() => {
                Event.$emit('toast', {text: 'Great! Changes saved successfully.'});
            })
            .catch((err) => {
                if (err.response?.data?.message) {
                    Event.$emit('toast', { text: err.response.data?.message, type: 'danger' });
                }
                console.error(err);
            })
            .then(() => {
                this.loading = false;
            });
        },
        reset() {
            this.type              = this.current.type;
            this.value.count       = this.current.count ?? this.options.count[0];
            this.value.interval    = this.current.interval ?? this.options.interval[0];
            this.value.hours       = Schedule.makeRawHours(this.current.hours ?? []);
            this.value.hoursByDays = Schedule.makeRawHoursByDays(this.current.hours_by_days ?? []);
        },
        makeSchedule() {
            switch (this.type) {
                case 'count':
                    return {type: 'count', count: this.value.count};

                case 'interval':
                    return {type: 'interval', interval: this.value.interval};

                case 'hours':
                    return {type: 'hours', hours: Schedule.makeHoursFromRaw(this.value.hours)};

                case 'hours_by_days':
                    return {
                        type: 'hours_by_days',
                        hours_by_days: this.value.hoursByDays.map(day => {
                            return {
                                day_of_week: day.dayOfWeek,
                                hours      : Schedule.makeHoursFromRaw(day.hours),
                            }
                        }),
                    };
            }

            return {};
        },
        makeSummary() {
            if (!this.summary.is_on || !this.can_update_weekly_schedule) return {
                state: 'off'
            };

            return {
                state: 'on',
                at   : Schedule.makeHourFromRaw(this.summary.at),
            };
        },
        makeDND() {
            if (!this.dnd.is_on || !this.can_update_weekly_schedule) return {
                state: 'off'
            };

            return {
                state: 'on',
                starts_at: Schedule.makeHourFromRaw(this.dnd.starts_at),
                ends_at: Schedule.makeHourFromRaw(this.dnd.ends_at)
            };
        },
    }
}
</script>
